// Offline messages inbox — visitors who tried to chat while no
// agents were available leave a message here via the public form.
// Access requires livechat.admin, matching the existing backend policy.

import { useMutation, useQuery } from "@tanstack/react-query";
import { getAuthToken, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Inbox, Mail, CheckCircle2 } from "lucide-react";
import { useAuth, hasPermission } from "@/lib/auth";
import { offlineInboxUrl, loadOfflineInbox, handleOfflineMessage, offlineInboxError, requestOfflineInbox } from "@/lib/offline-inbox";

const inboxRequest = (method: string, url: string) => requestOfflineInbox(method, url, getAuthToken());

export default function LiveChatOfflinePage() {
  const { user, organization } = useAuth();
  const allowed = hasPermission(user, "livechat.admin");
  const key = [offlineInboxUrl, organization?.id, user?.id];
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: key,
    queryFn: () => loadOfflineInbox(inboxRequest),
    enabled: allowed && !!organization?.id,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchInterval: 30000,
  });
  const markHandled = useMutation({
    mutationFn: async (id: string) => {
      return handleOfflineMessage(inboxRequest, id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/offline-notifications"] });
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: () => { void queryClient.invalidateQueries({ queryKey: key }); },
  });

  const visible = allowed && !isError && !!organization?.id;
  const newCount = visible ? data?.filter(m => m.status === "new").length ?? 0 : 0;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="page-offline-messages">
      <div className="flex items-center gap-3 mb-6">
        <Inbox className="w-6 h-6" />
        <h1 className="text-2xl font-bold">Offline Messages</h1>
        {newCount > 0 && <Badge data-testid="badge-new-count">{newCount} new</Badge>}
      </div>

      {!allowed || !organization?.id ? <p role="alert">Offline Messages requires an organization and live-chat admin permission.</p> : null}
      {allowed && organization?.id && isLoading && <p role="status" className="text-sm text-muted-foreground">Loading offline messages…</p>}
      {isError && (
        <div role="alert" className="mb-4 space-y-2">
          <p>{offlineInboxError(error)}</p>
          <Button variant="outline" disabled={isFetching} onClick={() => void refetch()}>Try again</Button>
        </div>
      )}
      {markHandled.isError && (
        <p role="alert" className="mb-4 text-sm text-destructive">Could not mark the message handled. {offlineInboxError(markHandled.error)}</p>
      )}
      {visible && !isLoading && data?.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <Mail className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No offline messages yet.
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {visible && data?.map((m) => (
          <Card key={m.id} data-testid={`card-offline-${m.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm" data-testid={`text-name-${m.id}`}>{m.name}</span>
                    <span className="text-xs text-muted-foreground" data-testid={`text-email-${m.id}`}>{m.email}</span>
                    {m.status === "new" ? (
                      <Badge variant="default" className="text-[10px]">New</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Handled</Badge>
                    )}
                  </div>
                  <p className="text-sm whitespace-pre-wrap" data-testid={`text-message-${m.id}`}>{m.messageBody}</p>
                  <p className="text-[11px] text-muted-foreground mt-2">
                    {m.createdAt ? new Date(m.createdAt).toLocaleString() : ""}
                  </p>
                </div>
                {m.status === "new" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markHandled.mutate(m.id)}
                    disabled={markHandled.isPending}
                    data-testid={`button-handle-${m.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />{markHandled.isPending && markHandled.variables === m.id ? "Marking handled…" : "Mark handled"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
